// @flow
import { option } from 'fp-ts';
import { compose } from 'fp-ts/lib/function';
import { get } from '../helpers/objects';
import { concat, split } from '../helpers/strings';
import { EventType } from '../flowtypes/event';
import { getVideoThumbs } from '../helpers/media';
import {
  getProductPrice,
  ProductAdt,
  getProductProperty,
  getConfigurableAttributes,
  getProductPropertyOr,
} from '../types/product';
import { getSku } from '../helpers/url';

// generateState :: EventType => Object
const generateState = (event: EventType) => {
  const bodyObject = get('body')(event);

  const body = typeof bodyObject === 'string' ? JSON.parse(bodyObject) : bodyObject;
  const productData = get('product')(body);
  const media = get('media')(body);
  const product = ProductAdt(productData);

  return getVideoThumbs(media).then((updatedMedia) => {
    const { addToCartUrl, urls } = body;

    return {
      rawProduct: productData,
      cachedFormKey: body.cachedFormKey,
      product: {
        sku: getProductPropertyOr('sku')('')(product),
        name: getProductPropertyOr('name')('')(product),
        description: getProductPropertyOr('description')('')(product),
        addToCartLabel: getProductPropertyOr('cta_label')('Add to Cart')(product),
        detailsHtml: getProductProperty('product_details')(product),
        shippingInfoHtml: getProductProperty('product_shipping_info')(product),
        productDetailsExtraLabel: getProductProperty('product_details_extra_label')(product),
        productDetailsExtraContent: getProductProperty('product_details_extra')(product),
        holidayNote: getProductProperty('holiday_note_disable')(product) > 0 ? getProductPropertyOr('holiday_note')(null)(product) : null,
        id: getProductProperty('entity_id')(product),
        price: getProductPrice(product),
        url: concat(urls.magento)(getProductProperty('canonical_url')(product)),
        mediaUrl: urls.media,
        enterEditor: parseInt(getProductProperty('cart_page')(product), 10) < 1,
        productType: getProductPropertyOr('custom_product_type')('')(product).toLowerCase(),
        addToCartUrl,
        livePreviewAttributes: (
          option.fromNullable(getProductProperty('live_preview_attributes')(product))
            .map(split(','))
            .getOrElseValue([])
        ),
        isMobileFriendly: getProductProperty('is_mobile_friendly')(product) === '1',
        mobileAvailability: getProductPropertyOr('mobile_availability')(null)(product),
        inStock: compose(x => x.map(get('is_in_stock')), option.fromNullable, getProductPropertyOr('quantity_and_stock_status')(null))(product)
          .getOrElseValue(null),
        priceList: getProductPropertyOr('advanced_pricing')(null)(product),
      },
      attributes: (
        option.fromNullable(getConfigurableAttributes(product))
          .map(p =>
            p.map(x => ({
              ...x,
              options: x.options && x.options[0].sort_order ?
                x.options.sort((a, b) => parseInt(a.sort_order, 10) - parseInt(b.sort_order, 10)) :
                x.options,
            }),
            ),
          )
          .getOrElseValue([])
      ),
      configuration: getProductPropertyOr('configuration')(null)(product),
      media: updatedMedia,
      quantity: {
        value: 1,
        error: null,
      },
    };
  });
};

const getDefaultState = (event: ?Object = null): Object => {
  const maybeEvent = option.fromNullable(event);
  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test') {
    return new Promise(resolve =>
      resolve(maybeEvent
        .map(generateState)
        .getOrElseValue(window.__PRELOADED_STATE__))).catch((e) => {
      throw e;
    });
  }

  // This gets stripped from production builds thanks to tree shaking.
  return fetch(`${process.env.REACT_APP_MAGENTO_URL}/au/product/${getSku()}/render-info`)
    .then(resp => resp.json().then((json) => {
      window.__CACHED_FORM_KEY__ = json.cached_form_key;
      const body = {
        body: {
          ...json,
          addToCartUrl: json.add_to_cart_url,
          product: JSON.parse(json.product_data[0]), // Temporary band-aid to parse array of one stringified object
          media: json.media_data.map(JSON.parse), // Temporary band-aid to parse array of stringified objects
          urls: JSON.parse(json.urls[0]), // Temporary band-aid to parse array of stringified objects
          cachedFormKey: json.cached_form_key,
        },
      };
      return generateState(body);
    }));
};


export default getDefaultState;



// WEBPACK FOOTER //
// ./src/store/defaultState.js